import React from "react";
import {toast} from "react-toastify";
import Card from "../../../components/Card";
import Button from "../../../components/Button";
import Loader from "../../../components/Loader";
import {
    useGetTaskStatsQuery,
    useGetFailedTasksQuery,
    useGetQueuesQuery,
    useGetWorkersQuery,
    useRetryTaskMutation,
    usePurgeQueueMutation,
    usePauseWorkersMutation,
    useResumeWorkersMutation,
} from "../../../storage/adminApi";
import localStyles from "../styles.module.scss";

const POLL_INTERVAL = 10000;

const AdminTasks = () => {
    const {data: stats, isLoading: statsLoading} = useGetTaskStatsQuery(undefined, {
        pollingInterval: POLL_INTERVAL,
    });
    const {data: failedTasks, isLoading: failedLoading} = useGetFailedTasksQuery(undefined, {
        pollingInterval: POLL_INTERVAL,
    });
    const {data: queues, isLoading: queuesLoading} = useGetQueuesQuery(undefined, {
        pollingInterval: POLL_INTERVAL,
    });
    const {data: workers, isLoading: workersLoading} = useGetWorkersQuery(undefined, {
        pollingInterval: POLL_INTERVAL,
    });

    const [retryTask] = useRetryTaskMutation();
    const [purgeQueue] = usePurgeQueueMutation();
    const [pauseWorkers] = usePauseWorkersMutation();
    const [resumeWorkers] = useResumeWorkersMutation();

    const handleRetry = async (taskId: number) => {
        try {
            await retryTask(taskId).unwrap();
            toast.success("Task retried successfully");
        } catch (e) {
            toast.error("Failed to retry task");
        }
    };

    const handlePurge = async (queueName: string) => {
        if (!window.confirm(`Purge queue "${queueName}"? This will remove all pending tasks.`)) return;
        try {
            await purgeQueue(queueName).unwrap();
            toast.success(`Queue "${queueName}" purged`);
        } catch (e) {
            toast.error("Failed to purge queue");
        }
    };

    const handlePause = async () => {
        try {
            await pauseWorkers().unwrap();
            toast.success("Workers paused");
        } catch (e) {
            toast.error("Failed to pause workers");
        }
    };

    const handleResume = async () => {
        try {
            await resumeWorkers().unwrap();
            toast.success("Workers resumed");
        } catch (e) {
            toast.error("Failed to resume workers");
        }
    };

    return (
        <div>
            <h4 className="fw-600" style={{marginBottom: 16}}>Tasks Management</h4>

            {/* Stats Cards */}
            <div className={localStyles.statsGrid}>
                <Card className={localStyles.statCard}>
                    <span className={localStyles.statLabel}>Active Tasks</span>
                    <span className={localStyles.statValue}>
                        {statsLoading ? "..." : (stats?.live?.active ?? 0)}
                    </span>
                </Card>
                <Card className={localStyles.statCard}>
                    <span className={localStyles.statLabel}>Reserved Tasks</span>
                    <span className={localStyles.statValue}>
                        {statsLoading ? "..." : (stats?.live?.reserved ?? 0)}
                    </span>
                </Card>
                <Card className={localStyles.statCard}>
                    <span className={localStyles.statLabel}>Queued Total</span>
                    <span className={localStyles.statValue}>
                        {statsLoading ? "..." : (stats?.live?.scheduled ?? 0)}
                    </span>
                </Card>
                <Card className={localStyles.statCard}>
                    <span className={localStyles.statLabel}>Workers Online</span>
                    <span className={localStyles.statValue}>
                        {statsLoading ? "..." : (stats?.live?.workers?.length ?? 0)}
                    </span>
                </Card>
            </div>

            {/* Worker Controls */}
            <div className={localStyles.workerControls}>
                <Button
                    id="pause-workers"
                    size="small"
                    background="bg-outline"
                    text="Pause All Workers"
                    click={handlePause}
                />
                <Button
                    id="resume-workers"
                    size="small"
                    background="bg-green"
                    text="Resume All Workers"
                    click={handleResume}
                />
            </div>

            {/* Queues */}
            <h5 className="fw-600" style={{marginBottom: 8}}>Queues</h5>
            <div className={localStyles.queueGrid}>
                {queuesLoading ? (
                    <Loader/>
                ) : (
                    queues?.map((q) => (
                        <Card key={q.name} className={localStyles.queueCard}>
                            <div>
                                <div className={localStyles.queueName}>{q.name}</div>
                                <span className={localStyles.queueBadge}>{q.count} tasks</span>
                            </div>
                            <Button
                                id={`purge-${q.name}`}
                                size="small"
                                background="bg-danger"
                                text="Purge"
                                click={() => handlePurge(q.name)}
                            />
                        </Card>
                    ))
                )}
            </div>

            {/* Failed Tasks */}
            <h5 className="fw-600" style={{marginBottom: 8}}>Failed Tasks</h5>
            {failedLoading ? (
                <Loader/>
            ) : (
                <div style={{overflowX: "auto"}}>
                    <table className={localStyles.table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Task Name</th>
                                <th>Entity ID</th>
                                <th>Error</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!failedTasks || failedTasks.length === 0) ? (
                                <tr>
                                    <td colSpan={6} className={localStyles.noData}>No failed tasks</td>
                                </tr>
                            ) : (
                                failedTasks.map((task) => (
                                    <tr key={task.id}>
                                        <td>{task.id}</td>
                                        <td>{task.task_name}</td>
                                        <td>{task.entity_id}</td>
                                        <td>
                                            <span className={localStyles.textEllipsis} style={{maxWidth: 300}}>
                                                {task.error}
                                            </span>
                                        </td>
                                        <td>{task.created_at}</td>
                                        <td>
                                            <Button
                                                id={`retry-task-${task.id}`}
                                                size="small"
                                                background="bg-outline"
                                                text="Retry"
                                                click={() => handleRetry(task.id)}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default <AdminTasks/>;
